import { HttpClient } from '@angular/common/http';
import { ElementRef, inject, Injectable } from '@angular/core';
import { AbstractControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { PlexEnvironment } from '@app/env';
import { DataDescriptor } from 'app/cem/project/services/data-descriptor.model';
import { ScreenBehaviorData } from 'app/cem/project/services/screen-behavior-data';
import { ProjectTypeId } from 'app/shared/data/project-enums';
import { Project, ProjectTypeScreenBehavior } from 'app/shared/models';
import { NameValue, NameValueInt } from 'app/shared/models/name-value';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ProjectScreenBehaviorService {
  private http = inject(HttpClient);
  public environment = inject(PlexEnvironment);

  private BASE_URL = this.environment.apiUrl;
  private tableName: string = 'ProjectTypeScreenBehaviors';
  public _behaviorsAll: any[] = [];

  private _Map: Map<ProjectTypeId, ProjectTypeScreenBehavior[]>;
  private _DataByElement: Map<string, ScreenBehaviorData> = new Map();

  public async getBehaviors(): Promise<ProjectTypeScreenBehavior[]> {
    if (this._behaviorsAll.length > 0) {
      return Promise.resolve(this._behaviorsAll);
    }

    const filter = '';
    const select = '?$select=ProjectTypeScreenBehaviorId,ProjectTypeId,ComponentSelector,AppFunctionDescription,FieldName,Readonly,Visible,Required,InputHint,DefaultValue,DefaultValueSource,AvailableValuesLIst,AvailableValuesSource';

    let url = `${this.BASE_URL}${this.tableName}${filter}${select}`;
    this._Map = null;
    var getResult = this.http
      .get(url).pipe(
        map((r2: any) => {
          return r2.value as ProjectTypeScreenBehavior[];
        }));

    getResult.subscribe((r3) => {
      let map: Map<ProjectTypeId, ProjectTypeScreenBehavior[]> = new Map();
      if (r3 && r3.length) {
        r3.forEach((item: ProjectTypeScreenBehavior) => {
          //GROUP BY ProjectTypeId
          let p: ProjectTypeId = item.ProjectTypeId || 0;
          const collection = map.get(p);
          if (!collection) {
            map.set(p, [item]);
          } else {
            collection.push(item);
          }
          this._Map = map;
        });
        this._behaviorsAll = r3;
      }
    });
    return getResult.toPromise();
  }

  setup(element: ElementRef,
    formConfig: { [key: string]: any },
    fieldMapping: { [key: string]: string },
    form: UntypedFormGroup,
    visibility: { [key: string]: boolean },
    overrides: ProjectTypeScreenBehavior[] = null): any {

    let m: Map<string, NameValue<AbstractControl>> = new Map();

    for (let name in fieldMapping) {
      if (fieldMapping.hasOwnProperty(name)) {
        m.set(name, this.controlFromName(fieldMapping[name], form));
      }
    }
    let reverseMap: Map<string, string> = new Map();
    m.forEach((pair: NameValue<AbstractControl>, friendly: string) => {
      reverseMap.set(pair.name, friendly);
    });

    let data: ScreenBehaviorData = new ScreenBehaviorData(m, form, reverseMap, visibility, formConfig, overrides);
    this._DataByElement.set(element.nativeElement.localName, data);
  }

  private controlFromName(name: string, form: UntypedFormGroup): NameValue<AbstractControl> {
    return { name, value: form.get(name) };
  }

  public clearSelections(projectTypeId: number,
    el: ElementRef,
    mapOfLists: Map<string, any[]>,
    emitEvent: boolean = false, project: Project,
    applyDefaultValues: boolean = true,
    applyDefaultValuesForNonMappedControls: boolean = true) {

    let data = this._DataByElement.get(el.nativeElement.localName);
    mapOfLists.forEach((list: any[], fieldName: string) => {
      let control = data.form.controls[fieldName];
      if (!control) {
        console.warn('control not found with fieldName: ' + fieldName);
      } else {
        let fullFieldName = data.formControlMapReverse.get(fieldName);
        let defValue = {};
        if (applyDefaultValues && fullFieldName) {
          defValue = this.getDefault(projectTypeId, fullFieldName, list, null, project);
        }
        control.patchValue(defValue, { emitEvent });

        control.markAsPristine();
      }
    });

    if (applyDefaultValuesForNonMappedControls) {
      data.formControlMap.forEach((ctrl: NameValue<AbstractControl>, fullName: string) => {
        if (!mapOfLists.has(ctrl.name)) {
          let defValue = {};
          let control = ctrl.value;
          let fullFieldName = fullName;
          defValue = this.getDefault(projectTypeId, fullFieldName, null, null, project);
          if (defValue) {
            defValue = (defValue as any).value;
          } else {
          }
          control.patchValue(defValue, { emitEvent });

          control.markAsPristine();
        }
      });
    }
  }

  public selectSingles(el: ElementRef, mapOf: Map<string, any[]>, emitEvent: boolean = false) {
    let data = this._DataByElement.get(el.nativeElement.localName);
    try {
      //FOR ALL DROP DOWNS WITH A SINGLE ITEM, SELECT IT
      mapOf.forEach((value: any[], fieldName: string) => {
        if (value && value.length === 1) {
          let newValue = {};
          Object.assign(newValue, value[0]);
          let control = data.form.controls[fieldName];
          if (!control) {
            console.warn('control not found with fieldName: ' + fieldName);
          } else {
            if ((fieldName === 'ProjectSubTypeIds') && (!value[0].IsActive)) {
              console.warn('selectSingles - ProjectSubTypeIds first value is not active... ignoring');
            } else {
              control.patchValue(newValue, { emitEvent });
            }
          }
        }
      });
    }
    catch (e) {
      console.warn('Error selecting single items:', e);
    }
  }

  public async ScreenBehaviorsByProjectType(projectType: ProjectTypeId): Promise<ProjectTypeScreenBehavior[]> {
    //this._behaviorSubject.
    await this.getBehaviors();
    if (this._Map) {
      return Promise.resolve(this._Map.get(projectType));
    }
    return Promise.resolve(null);
  }

  public async apply(
    projectType: ProjectTypeId, parentEl: ElementRef,
    behaviorOverrider: (fieldName: string, friendlyName: string, control: AbstractControl, defaultBehavior: ProjectTypeScreenBehavior) => ProjectTypeScreenBehavior = null
  ): Promise<boolean> {
    let data = this._DataByElement.get(parentEl.nativeElement.localName);
    var s = await this.ScreenBehaviorsByProjectType(projectType);

    //update screen Behaviors
    let overrides = data.overrides;
    if (overrides && overrides.length) {
      overrides = overrides.filter(p => p.ProjectTypeId === projectType);
    }
    if (s && s.length && overrides && overrides.length) {
      s = s.concat(overrides);
    }

    if (!s || !s.length) {
      if (overrides && overrides.length) {
        s = overrides;
      } else {
        return Promise.resolve(true);
      }
    }

    this.updateScreenBehaviors(s, parentEl, data, behaviorOverrider);
    return Promise.resolve(true);
  }

  public getDefault(projectType: ProjectTypeId, fullFieldName: string, source: NameValueInt[],
    defaultValue: NameValueInt = null,
    project: Project
  ): NameValue<any> {
    let result: NameValue<any> = {
      value: 0,
      name: 'Select ' + fullFieldName
    };
    if (projectType === null || projectType === undefined) {
      return defaultValue;
    }
    if (this._Map && this._Map.size > 0) {
      let pts = this._Map.get(projectType);
      if (!pts) {
        return defaultValue;
      }
      let oneType = pts.find(p => p.FieldName === fullFieldName);
      if (oneType) {
        let def = oneType.DefaultValue;
        if (def === null || def === undefined || def === '') {
          return defaultValue;
        }

        if (def && def.length) {
          def = def.trim();
        }

        if (def === '[FieldValue]') {
          if (project) {
            result.value = project[fullFieldName];
          }
          return result;
        } else if (def.startsWith('(') && def.endsWith(')')) {
          //this is a javascript function, for example: (new Date())
          //do an eval on it for the value
          try {
            result.value = eval(def);
            return result;
          } catch (e) {
            console.warn(`eval failed for default value of screen behavior field:${fullFieldName}`);
          }
        }

        //if default starts with a quote (' or "), then use value 0, and name: 'thedefault'
        //if default is numeric, then return: {name: 'Select ' + fieldName, value: defaultValue}
        //if source, find where source.name === defaultValue, if found return that
        if (def.startsWith("'") || def.startsWith('"')) {
          result.name = def;
          result.value = 0;
          return result;
        } else {
          //is numeric
          let defNum = Number(def);
          if (!isNaN(defNum)) {
            result.value = defNum;
            return result;
          }
          else if (source && source.length) {
            let sourceOne = source.find(s => s.name === def);
            if (sourceOne) {
              result = sourceOne;
              return result;
            } else {
              return defaultValue;
            }
          }
        }
      }
    } else {
      return defaultValue;
    }
  }

  public filter(behavior: ProjectTypeScreenBehavior, data: DataDescriptor): boolean {
    if (!data || !data.source) return false;
    if (!data.source.length) {
      return false;
    }
    let avail = behavior && behavior.AvailableValuesLIst && behavior.AvailableValuesLIst.trim();
    if (!avail) {
      data.filtered = data.source;
      return false;
    }

    let pieces = avail.split(',').map(piece => piece.trim());

    if (isNaN(Number(pieces[0][0]))) {
      data.filtered = data.source.filter((value) => {
        return pieces.includes(value[data.nameField]);
      });
    } else {
      let numPieces = pieces.map(m => Number(m));
      data.filtered = data.source.filter((value) => {
        return numPieces.includes(value[data.valueField]);
      });
    }
  }

  public updateScreenBehaviors(
    behaviors: ProjectTypeScreenBehavior[],
    parentEl: ElementRef,
    data: ScreenBehaviorData,
    behaviorOverrider: (fieldName: string, friendlyName: string, control: AbstractControl, defaultBehavior: ProjectTypeScreenBehavior) => ProjectTypeScreenBehavior = null
  ): void {
    if (!behaviors || !behaviors.length) return;
    let tagName = parentEl.nativeElement.localName;

    let myBehaviors = behaviors.filter(b => b.ComponentSelector === tagName);
    for (let i = 0; i < myBehaviors.length; i++) {
      let oneBehavior = myBehaviors[i];
      let fieldName = oneBehavior.FieldName;

      let altFieldName = data.formControlMap ? data.formControlMap.get(fieldName) : null;
      let control: AbstractControl;
      let nameUsed: string;

      if (altFieldName) {
        fieldName = altFieldName.name;
        control = altFieldName.value || data.form.get(fieldName);
      } else {
        control = data.form.controls[fieldName];
      }
      nameUsed = fieldName;
      if (control) {
        if (behaviorOverrider) {
          let copy = Object.assign({}, oneBehavior);
          oneBehavior = behaviorOverrider(fieldName, oneBehavior.FieldName, control, copy) || oneBehavior;
        }
        this.applyScreenBehaviors(control, oneBehavior, nameUsed, data, parentEl);
      }
    }
    data.form.updateValueAndValidity();
  }

  public applyScreenBehaviors(
    control: AbstractControl,
    behavior: ProjectTypeScreenBehavior,
    fieldName: string,
    data: ScreenBehaviorData, parentEl: ElementRef) {
    if (!control) return;
    let visible = behavior.Visible;

    let readonly = behavior.Readonly; //handled via disable()
    let required = behavior.Required; //handled via validators

    if (readonly) {
      control.disable({ emitEvent: false });
    } else {
      control.enable({ emitEvent: false });
    }

    let validators = this.getCustomValidators(fieldName, data.formConfig);

    if (required && visible) {
      validators.push(Validators.required);
    }

    if (validators && validators.length) {
      control.setValidators(validators);
    } else {
      control.clearValidators();
    }
    control.updateValueAndValidity({ emitEvent: false });

    data.visibility[fieldName] = visible;
  }

  public getCustomValidators(
    fieldName: string,
    config: { [key: string]: any }): ValidatorFn[] {
    let controlConfig = config && (config[fieldName] as any[]);
    if (controlConfig && controlConfig.length && controlConfig.length > 1) {
      let ve = controlConfig[1] as ValidatorFn[];
      if (ve && ve.length) {
        return ve;
      } else {
        return [];
      }
    } else {
      return [];
    }
  }

  public getVisibility(element: ElementRef, name: string) {
    let data = this._DataByElement.get(element.nativeElement.localName);
    return data.visibility[name];
  }

  public async getByProjectTypeId(id: ProjectTypeId): Promise<any> {
    if (this._behaviorsAll.length > 0) {
      return Promise.resolve(this._behaviorsAll.filter((f) => f.ProjectTypeId === id));
    }
    var filters = this._behaviorsAll.filter((f) => f.ProjectTypeId === id);
    return Promise.resolve(filters);
  }

  public getBehaviorRule(behaviors, ComponentSelector: string, FieldName: string) {
    return behaviors.find((b) => b.ComponentSelector === ComponentSelector && b.FieldName === FieldName);
  }
}